/**
 * Type definitions for the Time Series application
 */

// Base time series metadata
export interface TimeSeriesMeta {
    id: number;
    name: string;
    user_id?: number | string;
    description?: string;
    frequency: string; // 'daily', 'weekly', 'monthly', etc.
    tags?: string[];
}

// Data point in a time series
export interface TimeSeriesDataPoint {
    timestamp: string;
    value: number;
    metadata?: Record<string, any>;
}

// Convert DataPoint to TimeSeriesDataPoint
export function convertToTimeSeriesDataPoint(dataPoint: DataPoint): TimeSeriesDataPoint {
    const result: TimeSeriesDataPoint = {
        timestamp: dataPoint.timestamp instanceof Date ? dataPoint.timestamp.toISOString() : dataPoint.timestamp,
        value: dataPoint.value
    };

    if (dataPoint.metadata !== undefined) {
        result.metadata = dataPoint.metadata;
    }

    return result;
}

// Full time series with data points
export interface TimeSeriesData extends TimeSeriesMeta {
    data_points?: TimeSeriesDataPoint[];
}

// Forecast data point
export interface ForecastDataPoint {
    timestamp: string;
    value: number;
    confidence_lower?: number;
    confidence_upper?: number;
}

// ARIMA parameters
export interface ARIMAParams {
    p: number; // autoregressive order
    d: number; // differencing order
    q: number; // moving average order
}

// SARIMA parameters
export interface SARIMAParams extends ARIMAParams {
    P: number; // seasonal autoregressive order
    D: number; // seasonal differencing order
    Q: number; // seasonal moving average order
    m: number; // number of observations per seasonal cycle
}

// ETS parameters
export interface ETSParams {
    trend: 'add' | 'mul' | 'null' | null;
    seasonal: 'add' | 'mul' | 'null' | null;
    seasonal_periods?: number;
}

// Forecast parameters for API calls
export interface ForecastParams {
    algorithm: 'arima' | 'sarima' | 'ets' | string;
    horizon: number;
    confidence_level: number;
    parameters: ARIMAParams | SARIMAParams | ETSParams | Record<string, any>;
}

// Forecast data
export interface ForecastData {
    id: number | string;
    timeseries_id: number | string;
    algorithm: string;
    parameters?: Record<string, any>;
    start_date: string;
    end_date: string;
    created_at: string;
    forecast_points?: ForecastDataPoint[];
    metrics?: Record<string, any>;
}

// AI analysis data
export interface AIAnalysisData {
    id: number | string;
    timeseries_id: number | string;
    analysis_type: string;
    parameters?: Record<string, any>;
    results: Record<string, any>;
    created_at?: string; // Optional для обратной совместимости
}

// API response types
export interface PaginatedResponse<T> {
    data: T[];
    pagination: {
        total: number;
        skip: number;
        limit: number;
    };
}

// Chart data point for visualization
export interface DataPoint {
    timestamp: string | Date;
    value: number;
    metadata?: Record<string, string>;
}

// Chart forecast point for visualization
export interface ForecastPoint {
    timestamp: string | Date;
    value: number;
    lower?: number;
    upper?: number;
}

// CSV import options
export interface CSVImportOptions {
    timestamp_col: string;
    value_col: string;
    delimiter?: string;
    skip_header?: boolean;
    interpolation_method?: 'linear' | 'time' | 'index' | 'pad' | 'nearest' | 'zero' | 'slinear' | 'quadratic' | 'cubic' | 'spline' | 'barycentric' | 'polynomial' | 'krogh' | 'piecewise_polynomial' | 'spline' | 'pchip' | 'akima' | 'cubicspline' | 'none';
}

// OpenAI model interface
export interface OpenAIModel {
    model_id: string;   // The API identifier (e.g., gpt-4-turbo-preview)
    name: string;       // Display name (e.g., GPT-4 Turbo)
    description?: string;
    capabilities?: {
        text?: boolean;
        images?: boolean;
        audio?: boolean;
        code?: boolean;
        math?: boolean;
        reasoning?: boolean;
    };
    max_tokens?: number;
    token_limit?: number;
    cost_per_1k_tokens?: number;
    is_active: boolean;
    is_default?: boolean;
}

// AI Prompt interface
export interface AIPrompt {
    id: number;
    name: string;
    purpose: string;
    system_prompt: string;
    user_prompt: string;
    is_active: boolean;
}

// User interface
export interface User {
    id: number;
    username: string;
    email: string;
    role?: string;
}